import { useAuth } from '@/contexts/AuthContext';
import { useQuery } from '@tanstack/react-query';
import { partnerApi } from '@/lib/api';
import { useNavigate } from '@tanstack/react-router';
import { Building2, Users, TrendingUp, Zap } from 'lucide-react';
import styles from './DashboardPage.module.css';

export default function DashboardPage() {
  const { partnerUser } = useAuth();
  const navigate = useNavigate();

  const tenantsQ = useQuery({
    queryKey: ['partner', 'tenants'],
    queryFn: () => partnerApi.tenants.list({ limit: 100 }),
    enabled: !!partnerUser,
  });

  const tenants = tenantsQ.data?.data ?? [];
  const activeTenants = tenants.filter((t) => t.status === 'ACTIVE').length;
  const trialTenants  = tenants.filter((t) => t.status === 'TRIAL').length;

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.greeting}>{greeting}, {partnerUser?.name?.split(' ')[0] ?? 'Partner'} 👋</h1>
          <p className={styles.subtitle}>
            Managing <strong>{partnerUser?.partner?.name ?? 'your organisation'}</strong> — {tenants.length} tenant{tenants.length !== 1 ? 's' : ''} under your account
          </p>
        </div>
      </div>

      {/* Stats */}
      <div className={styles.stats}>
        {[
          { icon: <Building2 size={20} />, label: 'Total Tenants', value: String(tenantsQ.isLoading ? '—' : tenants.length), color: 'brand' },
          { icon: <TrendingUp size={20} />,  label: 'Active',        value: String(tenantsQ.isLoading ? '—' : activeTenants),  color: 'success' },
          { icon: <Zap size={20} />,         label: 'Trial',         value: String(tenantsQ.isLoading ? '—' : trialTenants),   color: 'warning' },
          { icon: <Users size={20} />,       label: 'Your Role',     value: partnerUser?.role ?? '—',                          color: 'muted' },
        ].map((s) => (
          <div key={s.label} className={`${styles.statCard} ${styles[`stat_${s.color}`]}`}>
            <div className={styles.statIcon}>{s.icon}</div>
            <div className={styles.statValue}>{s.value}</div>
            <div className={styles.statLabel}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Recent tenants */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Recent Tenants</h2>
          <button className={styles.viewAll} onClick={() => navigate({ to: '/tenants' })}>
            View all →
          </button>
        </div>
        {tenantsQ.isLoading ? (
          <div className={styles.loading}>Loading tenants…</div>
        ) : tenants.length === 0 ? (
          <div className={styles.empty}>
            <Building2 size={32} />
            <h3>No tenants yet</h3>
            <p>Create your first tenant to get started.</p>
            <button className={styles.ctaBtn} onClick={() => navigate({ to: '/tenants' })}>
              Create Tenant
            </button>
          </div>
        ) : (
          <div className={styles.tenantList}>
            {tenants.slice(0, 5).map((t) => (
              <div
                key={t.id}
                className={styles.tenantRow}
                onClick={() => navigate({ to: '/tenants/$tenantId', params: { tenantId: t.id } })}
              >
                <div className={styles.tenantAvatar}>{t.name[0]?.toUpperCase()}</div>
                <div className={styles.tenantInfo}>
                  <div className={styles.tenantName}>{t.name}</div>
                  <div className={styles.tenantSlug}>{t.slug}{t.allowedDomain ? ` · ${t.allowedDomain}` : ''}</div>
                </div>
                <span className={`${styles.badge} ${styles[`badge_${t.status.toLowerCase()}`]}`}>{t.status}</span>
                <span className={styles.planBadge}>{t.plan}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
